import {
  ArenaCountries,
  IArenaBetChips,
  GameType,
  IArena,
  IConfig,
  IGeneral,
  IMaintenance,
  IStreamDrmData,
  IStreamWatermarkData,
} from '@/utils/index.model';
import {
  getData,
  keysToCamel,
  patchData,
  postData,
  getItemFromStore,
  isIframeReload,
  getArenaStreamPlatform,
  getArenaStreamDrmCode,
} from '@/utils/helpers';
import { API_URLS, ARENA_GAME_TYPE, GC_ALERT_LOG_TYPE } from '@/utils/constant';
import { cloneDeep, findIndex, sortBy } from 'lodash';
import { sendWsRequest } from '@/utils/websocket';
import { getMaintenanceData } from '@/utils/maintenance';

interface IGeneralState {
  general: IGeneral;
}

let arenaStreamInterval: ReturnType<typeof setInterval> | null = null;
let arenaStreamArenaId = '';

/*
 * Per-arena stream plumbing, so two products can stream at once on desktop.
 *
 * The ArenaStream socket response carries no arena id (see the protobuf in
 * websocket-service.ts), so responses are correlated to requests FIFO: every
 * request pushes its arena id, every response shifts the oldest one. Responses
 * arrive in request order on a single socket, so this holds; if the queue ever
 * runs dry we fall back to the last arena asked, which is the old behaviour.
 *
 * `streamUrl` and friends stay as the "most recent response" for every existing
 * caller -- only components that pass an explicit arenaId read the per-arena map.
 */
const arenaStreamTimers: { [arenaId: string]: ReturnType<typeof setInterval> } = {};
let pendingStreamArenaIds: string[] = [];

const takePendingStreamArenaId = (): string => {
  const next = pendingStreamArenaIds.shift();
  return next || arenaStreamArenaId;
};

// const sortArenas = (arenas: IArena[]) => {
//   return sortBy(
//     arenas.map((ar: IArena) => {
//       ar.index = ArenaCountries[ar.code];
//       console.log('Arena:', ar.code, 'Index:', ar.index);
//       return ar;
//     }),
//     'index'
//   );
// };

const requestArenaStream = (context: any, arenaId: string) => {
  const token = context.rootGetters['general/token'];
  const { language } = context.rootGetters['general/config'];
  const platform = getArenaStreamPlatform();
  const drmType = getArenaStreamDrmCode();
  pendingStreamArenaIds.push(arenaId);
  sendWsRequest(token, GameType.arena_stream, {
    id: arenaId,
    lang: language,
    platform,
    drmType: drmType || 'wv',
  });
};

const getArenaCountry = (country: string) => {
  if (country === 'asean') return 'ph';
  return country;
};

const GENERAL_DATA: IGeneral = {
  inputPanelScaleRatio: {
    vertical: 0,
    landscape: 0,
  },
  balance: {
    value: 0,
    futureDiff: 0,
  },
  token: '',
  member: {
    displayName: '',
    currencyDisplayCode: '',
    currencySymbol: '',
    exchangeRate: '1',
  },
  errorHandler: {
    errorMessage: '',
    showPopup: false,
  },
  arenas: [],
  selectedArena: {} as IArena,
  config: {} as IConfig,
  videoAutoScale: false,
  theme: 'default',
  streamVersion: 0,
  pipDisabled: true,
  constrols: {
    historySelection: '',
  },
  lastIframeUrl: '',
  streamUrl: '',
  // arenaId -> { url, watermark, drm } for that arena's stream.
  arenaStreams: {} as { [arenaId: string]: any },
  /*
   * arenaId -> { betChips, customBetChip } from GET /arenas/:id/bet-chips.
   * The chip row is per arena now; `config.betChips` stays as the fallback for
   * an arena that has not answered yet (and for mobile's first paint).
   */
  arenaBetChips: {} as { [arenaId: string]: IArenaBetChips },
  streamError: '',
  maintenance: {} as IMaintenance,
  streamWatermarkData: {} as IStreamWatermarkData,
  streamDrmData: {} as IStreamDrmData,
  // floatingVideo: false
  serverTimeDiff: 0,
  selectedArenaCode: '',
  focus: '',
  lobbyUrl: '',
  isMuted: true,
  binaryDisabled: false,
  clientVersion: 1,
};
const storedGeneralData = getItemFromStore('general.general');
const applyArenaStreamToGlobals = (stateData: IGeneralState, data: any): void => {
  if (data.url) {
    stateData.general.streamUrl = data.url;
  } else {
    stateData.general.streamUrl = '';
  }

  stateData.general.streamWatermarkData = {
    url: data.url || '',
    watermark: data.watermark,
    watermarkFrequency: data.watermarkFrequency,
    watermarkMovement: data.watermarkMovement || 0,
    watermarkType: data.watermarkType || 0,
    watermarkImageUrl: data.watermarkImageUrl || '',
    watermarkFontSize: data.watermarkFontSize || 16,
  };
  stateData.general.streamDrmData = {
    drmLicenseUrl:
      data.drmLicenseUrl ||
      data.drm_license_url ||
      data.licenseUrl ||
      data.license_url ||
      data.drmLicense ||
      data.widevineLicenseUrl ||
      '',
    drmCertUrl:
      data.drmCertUrl || data.drm_cert_url || data.certUrl || data.cert_url || data.certificateUrl || data.drmCert || '',
  };
};

// Arenas whose chip request is in flight. `syncArenas` can fire several times
// in quick succession, and each pane asks for its own arena on mount, so the
// same arena was being requested three or four times on load.
const betChipsInFlight = new Set<string>();

/*
 * GET/PATCH /arenas/:id/bet-chips. Read defensively: the payload may be the
 * array itself, or an object carrying it under `betChips` / `bet_chips` /
 * `data`, with the custom chip alongside. Anything non-numeric is dropped
 * rather than rendered as a blank chip.
 */
const normalizeBetChips = (response: any): IArenaBetChips => {
  const payload = keysToCamel(response || {}) as any;
  const raw = Array.isArray(payload) ? payload : payload.betChips || payload.data || [];
  const betChips = (Array.isArray(raw) ? raw : [])
    .map((chip: any) => Number(chip && chip.amount !== undefined ? chip.amount : chip))
    .filter((chip: number) => Number.isFinite(chip) && chip > 0);
  return {
    betChips,
    customBetChip: Number(payload.customBetChip) || 0,
    // The arena's own bounds for the custom chip, which are not the member's
    // global per-fight limit.
    minBetChip: Number(payload.minBetChip) || 0,
    maxBetChip: Number(payload.maxBetChip) || 0,
  };
};

export const state = () => ({
  general: { ...(isIframeReload((storedGeneralData || {}).lastIframeUrl) ? storedGeneralData : GENERAL_DATA) },
});
export const mutations = {
  // UPDATE_STATUS_FLOATING_VIDEO(stateData: IGeneralState, value: boolean): void {
  //   stateData.general.floatingVideo = value
  // },
  UPDATE_GENERAL(stateData: IGeneralState, general: Partial<IGeneral>): void {
    stateData.general = {
      ...stateData.general,
      ...general,
    };
  },
  UPDATE_BALANCE_VALUE(stateData: IGeneralState, balance: number): void {
    stateData.general.balance.value = balance;
  },
  UPDATE_ODDS_TYPE(stateData: IGeneralState, oddsType: string): void {
    stateData.general.config.oddsType = oddsType;
  },
  UPDATE_FLOAT_VIDEO(stateData: IGeneralState, value: boolean): void {
    stateData.general.config.floatVideo = value;
  },
  UPDATE_CUSTOM_BET_CHIP(stateData: IGeneralState, value: number): void {
    stateData.general.config.customBetChip = value;
  },
  UPDATE_BALANCE_DIFF(stateData: IGeneralState, diff: number): void {
    stateData.general.balance.futureDiff = diff;
  },
  UPDATE_BALANCE(stateData: IGeneralState, balance: { value: number; futureDiff: number }): void {
    stateData.general.balance = { ...balance };
  },
  RESET_BALANCE(stateData: IGeneralState) {
    stateData.general.balance = {
      value: 0,
      futureDiff: 0,
    };
  },
  RESET_GENERAL(stateData: IGeneralState) {
    stateData.general = { ...GENERAL_DATA };
  },
  RESET_SELECTED_ARENA(stateData: IGeneralState) {
    stateData.general.selectedArena = {} as IArena;
  },
  UPDATE_ARENA_STATUS(stateData: IGeneralState, arenas: IArena[]) {
    stateData.general.arenas = [...arenas];
  },
  INSERT_ARENA(stateData: IGeneralState, arena: IArena) {
    const targetIndex = findIndex(stateData.general.arenas, (ar: IArena) => ar.id === arena.id);
    if (targetIndex >= 0) {
      stateData.general.arenas.splice(targetIndex, 1, arena);
    } else {
      stateData.general.arenas = [...stateData.general.arenas, arena];
    }
  },
  REMOVE_ARENA(stateData: IGeneralState, id: string) {
    const targetIndex = findIndex(stateData.general.arenas, (ar: IArena) => ar.id === id);
    if (targetIndex >= 0) {
      stateData.general.arenas.splice(targetIndex, 1);
    }
  },
  UPDATE_ARENA_STREAM(stateData: IGeneralState, data: any) {
    applyArenaStreamToGlobals(stateData, data);
  },
  /*
   * A real ArenaStream response off the socket.
   *
   * Separate from UPDATE_ARENA_STREAM because that one is also used to *clear*
   * the stream locally (tai-xiu-full-video.vue does it on unmount). Only an
   * actual response may consume the pending-request queue -- letting a manual
   * clear shift it would file the empty payload under the wrong arena and
   * desync every correlation after it.
   */
  UPDATE_ARENA_BET_CHIPS(stateData: IGeneralState, { arenaId, ...entry }: IArenaBetChips & { arenaId: string }) {
    if (!arenaId) return;
    stateData.general.arenaBetChips = {
      ...stateData.general.arenaBetChips,
      [arenaId]: { ...stateData.general.arenaBetChips[arenaId], ...entry },
    };
  },
  RECEIVE_ARENA_STREAM(stateData: IGeneralState, data: any) {
    const payload = data || {};
    const arenaId = payload.arenaId || takePendingStreamArenaId();

    applyArenaStreamToGlobals(stateData, payload);

    if (!arenaId) return;
    stateData.general.arenaStreams = {
      ...stateData.general.arenaStreams,
      [arenaId]: {
        url: payload.url || '',
        watermark: payload.watermark,
        watermarkFrequency: payload.watermarkFrequency,
        watermarkMovement: payload.watermarkMovement || 0,
        watermarkType: payload.watermarkType || 0,
        watermarkImageUrl: payload.watermarkImageUrl || '',
        watermarkFontSize: payload.watermarkFontSize || 16,
        drmLicenseUrl: payload.drmLicenseUrl || payload.drm_license_url || payload.licenseUrl || '',
        drmCertUrl: payload.drmCertUrl || payload.drm_cert_url || payload.certUrl || '',
      },
    };
  },
  /*
   * The main socket was replaced: requests in flight on the old one will never
   * be answered, so their queued arena ids must go or every later response is
   * filed under the wrong arena.
   */
  RESET_PENDING_ARENA_STREAMS() {
    pendingStreamArenaIds = [];
  },
  /*
   * An ArenaStream response that carries no stream (an error other than the
   * min-balance one) still answers the oldest request; skipping it would shift
   * every later response onto the wrong arena.
   */
  DROP_ARENA_STREAM_RESPONSE(stateData: IGeneralState) {
    const arenaId = takePendingStreamArenaId();
    if (!arenaId) return;
    stateData.general.arenaStreams = {
      ...stateData.general.arenaStreams,
      [arenaId]: { url: '' },
    };
  },
  UPDATE_ARENA_STREAM_ERROR(stateData: IGeneralState, errorMessage: string) {
    stateData.general.streamError = errorMessage;
  },
  UPDATE_MAINTENANCE_STATUS(stateData: IGeneralState, data: IMaintenance) {
    stateData.general.maintenance = data;
  },
  UPDATE_LOBBY_URL(stateData: IGeneralState, url: string) {
    stateData.general.lobbyUrl = url;
  },
  UPDATE_MUTED(stateData: IGeneralState, isMuted: boolean) {
    stateData.general.isMuted = isMuted;
  },
};

export const actions = {
  // updateStatusFloatingVideo(context: any, value: boolean): void {
  //   context.commit('UPDATE_STATUS_FLOATING_VIDEO', value );
  // },
  updateGeneral(context: any, general: Partial<IGeneral>): void {
    context.commit('UPDATE_GENERAL', general);
  },
  resetGeneral(context: any): void {
    context.commit('RESET_GENERAL');
  },
  updateBalance(context: any, balance: { value: number; futureDiff: number }): void {
    context.commit('UPDATE_BALANCE', balance);
  },
  updateBalanceValue(context: any, balance: number): void {
    context.commit('UPDATE_BALANCE_VALUE', balance);
  },
  updateBalancefutureDiff(context: any, diff: number): void {
    context.commit('UPDATE_BALANCE_DIFF', diff);
  },
  getWallet(context: any): void {
    const { state } = context;
    const { token } = state.general;
    if (!token) return;
    getData(API_URLS.BALANCE, token)
      .then((res) => {
        const { balance } = res;
        context.commit('UPDATE_BALANCE_VALUE', balance);
      })
      .catch((e) => console.log(e));
  },
  updateWallet(context: any, balance: number): void {
    const { state } = context;
    const { balance: oldBalance } = state.general;
    let newBalance = Number(oldBalance.value) + Number(oldBalance.futureDiff);
    if (Number(balance) !== newBalance) {
      if (balance <= newBalance || newBalance === 0) {
        newBalance = Number(balance);
        context.commit('UPDATE_BALANCE', {
          value: newBalance,
          futureDiff: 0,
        });
      } else if (Number(balance) > newBalance) {
        const newDiff = Number(balance) - newBalance;
        context.commit('UPDATE_BALANCE_DIFF', newDiff);
      }
    }
  },
  getArenas(context: any): void {
    const { state } = context;
    const { token, arenas: oldArenas } = state.general;
    const { language } = context.rootGetters['general/config'];
    const { selectedArenaCode, focus } = state.general;
    getData(
      API_URLS.ARENAS +
        '?has_active_match_day=true' +
        `&lang=${language}` +
        `&game_type=${ARENA_GAME_TYPE.cockFight}`,
      token
    )
      .then((res) => {
        const arenas = keysToCamel(res);
        const arenaStatusList: { [arenaId: string]: string | undefined } = {};
        if (oldArenas.length > 0) {
          oldArenas.forEach((a: IArena) => (arenaStatusList[a.id] = a.fightStatus));
        }
        arenas.map((ar: any) => {
          ar.currentMatchDayId = ar.currentMatchDay.id;
          ar.fightType = ar.currentMatchDay.fightType;
          ar.isReverse = ar.currentMatchDay.isReverse;
          ar.fightStatus = arenaStatusList[ar.id];
          if(ar.currentMatchDay.endDateTime) {
            ar.isMatchDayEnding = true;
          }
          delete ar.currentMatchDay;
        });
        const matchedArena = arenas.find((a: IArena) => a.code === selectedArenaCode);
        if (matchedArena && matchedArena.id !== state.general.selectedArena.id) {
          context.commit('UPDATE_GENERAL', {
            selectedArena: { ...matchedArena },
          });
        } else {
          if (focus) {
            const focusArenas = arenas.filter((a: IArena) => getArenaCountry(a.country) === focus);
            if (focusArenas.length > 0) {
              context.commit('UPDATE_GENERAL', {
                selectedArena: { ...focusArenas[focusArenas.length - 1] },
                selectedArenaCode: focusArenas[focusArenas.length - 1].code,
              });
            }
          }
        }
        const currentSelectedArena = state.general.selectedArena;
        const updateSelectedArena = arenas.find((a: IArena) => a.id === currentSelectedArena.id);
        const stillExists = currentSelectedArena && arenas.some((a: IArena) => a.id === currentSelectedArena.id);
        const newSelectedArena = stillExists ? updateSelectedArena : arenas[arenas.length - 1];
        context.commit('UPDATE_GENERAL', {
          arenas: arenas,
          selectedArena: { ...newSelectedArena },
          selectedArenaCode: newSelectedArena.code,
        });
      })
      .catch((e) => console.log(e));
  },
  resetSelectedArena(context: any): void {
    context.commit('RESET_SELECTED_ARENA');
  },
  resetBalance(context: any): void {
    context.commit('RESET_BALANCE');
  },

  updateFloatVideo(context: any, value: boolean): void {
    const token = context.rootGetters['general/token'];
    patchData(API_URLS.CONFIG, token, { float_video: value }).then(() => {
      context.commit('UPDATE_FLOAT_VIDEO', value);
    });
  },
  updateOddsType(context: any, oddsType: string): void {
    const token = context.rootGetters['general/token'];
    patchData(API_URLS.CONFIG, token, { odds_type: oddsType }).then(() => {
      context.commit('UPDATE_ODDS_TYPE', oddsType);
    });
  },
  /*
   * The arena's own chip set. Called when a pane (or the mobile page) points at
   * an arena; the response is cached per arena, so switching back and forth
   * does not refetch.
   */
  async getArenaBetChips(context: any, arenaId: string): Promise<void> {
    if (!arenaId || betChipsInFlight.has(arenaId)) return;
    betChipsInFlight.add(arenaId);
    const token = context.rootGetters['general/token'];
    try {
      const response = await getData(`${API_URLS.ARENAS}${arenaId}/${API_URLS.ARENA_BET_CHIPS}`, token);
      context.commit('UPDATE_ARENA_BET_CHIPS', { arenaId, ...normalizeBetChips(response) });
    } catch (error) {
      // The global `config.betChips` stays in use; the chip row is never empty.
      console.log('[arena bet-chips]', arenaId, error);
    } finally {
      betChipsInFlight.delete(arenaId);
    }
  },
  /*
   * The member's custom chip for this arena. The response is used rather than
   * the sent value, so the backend stays the source of truth for clamping.
   */
  async updateArenaCustomBetChip(context: any, { arenaId, value }: { arenaId: string; value: number }): Promise<void> {
    if (!arenaId) return;
    const token = context.rootGetters['general/token'];
    // Optimistic: the field is being typed in, so it has to answer immediately.
    context.commit('UPDATE_ARENA_BET_CHIPS', { arenaId, customBetChip: Number(value) });
    try {
      const response = await patchData(`${API_URLS.ARENAS}${arenaId}/${API_URLS.ARENA_BET_CHIPS}`, token, {
        custom_bet_chip: Number(value),
      });
      const normalized = normalizeBetChips(response);
      context.commit('UPDATE_ARENA_BET_CHIPS', {
        arenaId,
        ...normalized,
        // The server is the authority on the saved value; fall back to what was
        // sent only if it answers without one.
        customBetChip: normalized.customBetChip || Number(value),
      });
    } catch (error) {
      console.log('[arena bet-chips patch]', arenaId, error);
    }
  },
  updateCustomBetChip(context: any, value: number): void {
    const token = context.rootGetters['general/token'];
    patchData(API_URLS.CONFIG, token, { custom_bet_chip: value }).then(() => {
      context.commit('UPDATE_CUSTOM_BET_CHIP', value);
    });
  },
  updateArenaStatus(context: any, arena: Partial<IArena>): void {
    const { state } = context;
    const { arenas, selectedArena } = state.general;

    const arenasTemp = cloneDeep(arenas);
    arenasTemp.map((a: IArena) => {
      if (a.id === arena.id) {
        a.fightStatus = arena.fightStatus;
        a.isReverse = !!arena.isReverse;
      }
      return a;
    });

    context.commit('UPDATE_ARENA_STATUS', arenasTemp);

    if (selectedArena && selectedArena.id === arena.id) {
      context.commit('UPDATE_GENERAL', {
        selectedArena: {
          ...selectedArena,
          fightStatus: arena.fightStatus,
          isReverse: !!arena.isReverse,
        },
      });
    }
  },
  insertArena(context: any, arena: IArena): void {
    context.commit('INSERT_ARENA', arena);
  },
  removeArena(context: any, id: string): void {
    context.commit('REMOVE_ARENA', id);
  },
  getArenaStreamUrl(context: any, id: string): void {
    // One refresh timer per arena: desktop keeps two streams alive at once, so a
    // second arena starting must not cancel the first one's refresh.
    if (arenaStreamTimers[id]) {
      clearInterval(arenaStreamTimers[id]);
      delete arenaStreamTimers[id];
    }
    if (arenaStreamInterval) {
      clearInterval(arenaStreamInterval);
      arenaStreamInterval = null;
    }

    requestArenaStream(context, id);

    arenaStreamArenaId = id;
    arenaStreamTimers[id] = setInterval(() => {
      requestArenaStream(context, id);
    }, 240000);
  },
  async getMaintenanceStatus(context: any): Promise<void> {
    await getMaintenanceData(API_URLS.MAINTENANCE).then((res) => {
      context.commit('UPDATE_MAINTENANCE_STATUS', res);
    });
  },

  updateArenaStream(context: any, data: any): void {
    context.commit('UPDATE_ARENA_STREAM', data);
  },

  setLobbyUrl(context: any, url: string): void {
    context.commit('UPDATE_LOBBY_URL', url);
  },

  // Pass arenaId to stop only that arena's polling, so a product unmounting after
  // another one mounted doesn't cancel the newly started refresh.
  stopArenaStreamPolling(context: any, arenaId?: string): void {
    if (arenaId) {
      if (arenaStreamTimers[arenaId]) {
        clearInterval(arenaStreamTimers[arenaId]);
        delete arenaStreamTimers[arenaId];
      }
      // Only tear down the legacy shared timer if it was this arena's.
      if (arenaId !== arenaStreamArenaId) return;
    } else {
      Object.keys(arenaStreamTimers).forEach((id) => {
        clearInterval(arenaStreamTimers[id]);
        delete arenaStreamTimers[id];
      });
      pendingStreamArenaIds = [];
    }
    if (arenaStreamInterval) {
      clearInterval(arenaStreamInterval);
      arenaStreamInterval = null;
    }
  },

  sendMetricsLog(context: any, type: number): void {
    const { state } = context;
    const { token } = state.general;
    if (!token) {
      return;
    }

    postData(API_URLS.METRICS_LOGS, token, { type }).catch((e) =>
      console.error('[MetricsLog] Error sending metrics log:', e)
    );
  },
};

export const getters = {
  floatingVideo: (stateData: any): boolean => {
    return stateData.general.floatingVideo;
  },
  inputPanelScaleRatio: (
    stateData: any
  ): {
    vertical: number;
    landscape: number;
  } => {
    return stateData.general.inputPanelScaleRatio;
  },
  balance: (stateData: any): number => {
    return stateData.general.balance.value;
  },
  futureDiff: (stateData: any): number => {
    return stateData.general.balance.futureDiff;
  },
  realBalance: (stateData: any): number => {
    return Number(stateData.general.balance.value) + Number(stateData.general.balance.futureDiff);
  },
  token: (stateData: any): string => {
    return stateData.general.token;
  },
  errorHandler: (stateData: any): string => {
    return stateData.general.errorHandler;
  },
  arenas: (stateData: any): IArena[] => {
    return stateData.general.arenas;
  },
  selectedArena: (stateData: any): IArena => {
    return stateData.general.selectedArena;
  },
  config: (stateData: any): IConfig => {
    return stateData.general.config;
  },
  videoAutoScale: (stateData: any): boolean => {
    return stateData.general.videoAutoScale;
  },
  member: (
    stateData: any
  ): {
    displayName: string;
    currencyDisplayCode: string;
    exchangeRate: string;
  } => {
    return stateData.general.member;
  },
  theme: (stateData: any): string => {
    return stateData.general.theme;
  },
  oddsType: (stateData: any): string => {
    return stateData.general.config.oddsType;
  },
  availableOddsType: (stateData: any): string => {
    return stateData.general.config.availableOddsType;
  },
  timezone: (stateData: any): string => {
    return stateData.general.config.timezone;
  },
  withCommission: (stateData: any): boolean => {
    return stateData.general.config.withCommission;
  },
  historySelection: (stateData: any): string => {
    return stateData.general.constrols.historySelection;
  },
  hasCommission: (stateData: any): boolean => {
    return stateData.general.config.hasCommission;
  },
  debug: (stateData: any): boolean => {
    return stateData.general.config.debug;
  },
  /*
   * The chip row for an arena: its own set once the endpoint has answered,
   * the global config until then. Callers pass the arena they are showing, so
   * two desktop panes on two arenas get two different rows.
   */
  arenaBetChips: (stateData: any) => (arenaId: string): number[] => {
    const entry = arenaId && stateData.general.arenaBetChips[arenaId];
    if (entry && entry.betChips && entry.betChips.length) return entry.betChips;
    return (stateData.general.config.betChips || []).map((chip: any) => Number(chip));
  },
  /*
   * The arena's own bounds for the custom chip (`min_bet_chip` /
   * `max_bet_chip`). Zero means the arena has not answered, and the caller
   * keeps the global limits.
   */
  arenaBetChipLimits: (stateData: any) => (arenaId: string): { min: number; max: number } => {
    const entry = arenaId && stateData.general.arenaBetChips[arenaId];
    return { min: Number(entry && entry.minBetChip) || 0, max: Number(entry && entry.maxBetChip) || 0 };
  },
  arenaCustomBetChip: (stateData: any) => (arenaId: string): number => {
    const entry = arenaId && stateData.general.arenaBetChips[arenaId];
    if (entry && entry.customBetChip) return Number(entry.customBetChip);
    return Number(stateData.general.config.customBetChip) || 0;
  },
  // The stream recorded for a specific arena, for components that own one
  // (a desktop pane). Everything else keeps reading `streamUrl`.
  arenaStream: (stateData: any) => (arenaId: string): any =>
    (arenaId && stateData.general.arenaStreams[arenaId]) || null,
  streamUrl: (stateData: any): string => {
    return stateData.general.streamUrl;
  },
  maintenance: (stateData: any): IMaintenance => {
    return stateData.general.maintenance;
  },
  enableJackpot: (stateData: any): boolean => {
    return stateData.general.config.enableJackpot;
  },
  streamVersion: (stateData: any): number => {
    return stateData.general.streamVersion || 0;
  },
  streamError: (stateData: any): string => {
    return stateData.general.streamError;
  },
  streamWatermarkData: (stateData: any): IStreamWatermarkData => {
    return stateData.general.streamWatermarkData;
  },
  streamDrmData: (stateData: any): IStreamDrmData => {
    return stateData.general.streamDrmData;
  },
  serverTimeDiff: (stateData: any): number => {
    return stateData.general.serverTimeDiff || 0;
  },
  hasSignificantTimeDiff: (stateData: any): boolean => {
    const diff = stateData.general.serverTimeDiff || 0;
    // Returns true if difference > 5 minutes (300000ms)
    return Math.abs(diff) > 300000;
  },
  pipDisabled: (stateData: any): boolean => {
    return stateData.general.pipDisabled || true;
  },
  lobbyUrl: (stateData: any): string => {
    return stateData.general.lobbyUrl;
  },
  isMuted: (stateData: any): boolean => {
    return stateData.general.isMuted;
  },
  binaryDisabled: (stateData: any): boolean => {
    return stateData.general.binaryDisabled;
  },
  clientVersion: (stateData: any): number => {
    return stateData.general.clientVersion || 1;
  },
  isClientV2: (stateData: any): boolean => {
    return (stateData.general.clientVersion || 1) === 2;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
